import React from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, Truck, ClipboardList, ClipboardCheck, FileBarChart, Database, Users, ScrollText } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-link-dashboard" },
  { to: "/dispatch", label: "Dispatch Entry", icon: Truck, testid: "nav-link-dispatch" },
  { to: "/register", label: "Register", icon: ClipboardList, testid: "nav-link-register" },
  { to: "/pod", label: "POD", icon: ClipboardCheck, testid: "nav-link-pod" },
  { to: "/reports", label: "Reports", icon: FileBarChart, testid: "nav-link-reports" },
  { to: "/masters", label: "Masters", icon: Database, testid: "nav-link-masters" },
  { to: "/users", label: "Users", icon: Users, testid: "nav-link-users", adminOnly: true },
  { to: "/audit", label: "Audit Log", icon: ScrollText, testid: "nav-link-audit" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV.filter((n) => !n.adminOnly || user?.role === "admin");

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-lg bg-blue-600 text-white grid place-items-center font-heading font-bold">D</div>
              <div className="leading-tight">
                <div className="font-heading font-bold text-slate-900">Dispatch Register</div>
                <div className="text-[11px] text-slate-500 -mt-0.5">Bulk Dairy Operations</div>
              </div>
            </div>
            <nav className="hidden lg:flex items-center gap-1">
              {items.map(({ to, label, icon: Icon, testid }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" /> {label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="text-sm font-medium text-slate-900">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">{user?.role}</div>
            </div>
            <Button variant="outline" size="sm" data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut className="h-4 w-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
        {/* mobile nav */}
        <nav className="lg:hidden flex overflow-x-auto gap-1 px-3 pb-2 border-t border-slate-100">
          {items.map(({ to, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} data-testid={testid + "-mobile"}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${
                  isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100"
                }`
              }>
              <Icon className="h-3.5 w-3.5" /> {label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="flex-1 max-w-[1400px] w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Outlet />
      </main>
    </div>
  );
}
